// brain/studyAvailability.js — LA función de disponibilidad de estudio por
// (modo, docs) — F6.5b "la puerta offline" (hallazgo del smoke de fuego: el
// corte encendió el motor detrás de una puerta cerrada — los gates de
// entrada de la era motor-en-servidor no se enteraron).
//
// UNA sola decisión para todas las pantallas: responde disponible o
// no-disponible con RAZÓN NOMBRADA. Los gates de UI consumen ESTA función —
// un solo lugar donde F6.7 enchufa la regla de negocio de D-G, sin volver a
// tocar pantallas.
//
// Superficie estática SEGURA del barrel del cerebro: importa solo el switch
// del corte y la conexión de bodega (liviana, no-op en web) — cero worker,
// cero chunks pesados.

import { openWarehouse } from '../warehouse/connection';
import { adaptiveRunsLocally, examRunsLocally, flashcardsRunLocally } from './cutover';

// Razones nombradas de no-disponibilidad. El copy por razón vive en las
// pantallas (i18n); aquí solo la semántica.
export const STUDY_UNAVAILABLE_REASON = {
  // El modo requiere red: los modos locales pre-corte / en web conservan su
  // gate de siempre. Con los tres cortes encendidos (F6.6b) ningún modo cae
  // aquí en la app — la razón sigue viva para la web y para un recule de
  // flag.
  NO_CONNECTION_FOR_MODE: 'no_connection_for_mode',
  // La bodega no tiene el pool completo del doc. Reúsa el contrato de
  // visibilidad-por-fila de F6.4: la fila de `documents` se escribe AL FINAL
  // de la descarga (pool completo + hash) — fila presente == pool listo por
  // construcción; no hay otro chequeo que hacer.
  POOL_NOT_READY: 'pool_not_ready',
  // D-G (FIRMADA por Carlos 2026-07-21: offline para TODOS los planes 30
  // días desde el estreno y después SOLO Pro) — SE ACTIVA EN F6.7. Razón
  // RESERVADA: hoy NINGÚN camino la retorna — cero lógica de plan, cero UI
  // de venta. F6.7 enchufa aquí la regla de negocio y las pantallas que ya
  // consumen esta función la muestran sin tocarse.
  OFFLINE_REQUIRES_PRO: 'offline_requires_pro',
};

// Fila presente en `documents` == pool completo con hash (contrato de
// visibilidad F6.4). Cualquier fallo de bodega cuenta como "no listo" —
// jamás se promete estudio local sin pool verificable.
async function localPoolReady(documentIds) {
  try {
    const api = await openWarehouse();
    const ph = documentIds.map(() => '?').join(', ');
    // TANDA M (M6): `locked_at IS NULL` — un documento BLOQUEADO por una bajada
    // de plan conserva su pool completo (bodega v12) pero NO se puede estudiar.
    // Sin esta cláusula, conservar el material habría abierto una puerta que
    // antes no existía: la fila presente significaba "pool listo" y ahora
    // también puede significar "pool listo pero fuera del plan". Es la mitad
    // "inusable" del bloqueo; la "invisible" vive en `screenSources::readDocs`,
    // que es lo que hace que este caso casi nunca se alcance — casi, y por eso
    // el candado está aquí también.
    const rows = await api.query(
      `SELECT id FROM documents WHERE id IN (${ph}) AND locked_at IS NULL;`,
      documentIds
    );
    return rows.length === documentIds.length;
  } catch (e) {
    return false;
  }
}

// Disponibilidad de un modo que corre 100% LOCAL (D-A): la red es
// irrelevante; lo único que manda es el pool en bodega. UNA sola fuente para
// las tres ramas locales (adaptive, flashcards y —desde F6.6b— exam).
async function localModeAvailability(documentIds) {
  if (!documentIds || documentIds.length === 0) return { available: true };
  if (await localPoolReady(documentIds)) return { available: true };
  // Con red, el pool está bajando (el downloader corre solo tras procesar);
  // sin red, no hay de dónde: la misma razón honesta en ambos casos.
  return { available: false, reason: STUDY_UNAVAILABLE_REASON.POOL_NOT_READY };
}

/**
 * ¿Se puede INICIAR una sesión de `mode` sobre `documentIds` ahora mismo?
 * → { available: true } | { available: false, reason: <razón nombrada> }
 *
 * - Adaptive / Flashcards / Exam en app con su corte encendido: corren 100%
 *   LOCAL (D-A / F6.6a / F6.6b) — la red es irrelevante; lo único que manda
 *   es el pool. Exam exige preguntas>0 en el teléfono igual que adaptive
 *   (MODE_MATERIAL.exam = 'questions', deliveryState.js), así que comparte
 *   la MISMA rama sin condición propia.
 * - Los modos locales pre-corte / web: siguen necesitando conexión — el gate
 *   de siempre, con razón nombrada por modo.
 * - `offline` = el dato de la pantalla vino de la bodega (data.offline de
 *   los ensambladores F6.4b) — el muestreo de conectividad vigente.
 * - Selección vacía → disponible: ese guard es de la pantalla (toast de
 *   "selecciona al menos uno"), no una razón de conectividad.
 */
export async function studyAvailability({ mode, documentIds, offline }) {
  if (mode === 'adaptive' && adaptiveRunsLocally()) {
    return localModeAvailability(documentIds);
  }
  // F6.6a: la rama de flashcards va ANTES del gate offline — en app con el
  // corte y pool listo, el mazo abre sin red.
  if (mode === 'flashcards' && flashcardsRunLocally()) {
    return localModeAvailability(documentIds);
  }
  // F6.6b: y la de EXAM. Con esta rama el gate NO_CONNECTION_FOR_MODE de
  // examen muere en la app — y con él el `return` temprano de
  // StudyCenter::startSession que hacía INALCANZABLE el candado D-G para
  // examen. Ese enchufe se activa aquí sin tocar proGate.js ni la pantalla.
  if (mode === 'exam' && examRunsLocally()) {
    return localModeAvailability(documentIds);
  }
  if (offline) {
    return { available: false, reason: STUDY_UNAVAILABLE_REASON.NO_CONNECTION_FOR_MODE };
  }
  return { available: true };
}
